import AddLocationIcon from '@mui/icons-material/AddLocation';
import { IconButton, Stack } from '@mui/material';
import { DateWrapper, HeaderAndContainer, Loader, Tooltip } from '@trucktrax/trucktrax-common';
import {
  OrderDto,
  PagedEntityDto,
  ProductLine,
  TicketDto,
  TruckDto,
  UrlKeyDto,
  UserRegionDto,
} from '@trucktrax/trucktrax-ts-common';
import { isEqual } from 'lodash';
import { AxiosResponse } from 'axios';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import type { Location as BrowserLocation, History } from 'history';
import { TRUCKS_PATH } from '../../../constants/apiConstants';
import {
  DEFAULT,
  READYMIX_STRING,
  READYMIX_VALUE,
  SUCCESS,
  TICKETS_SELECTED,
  TOOLTIP_REMOVE_DEFAULT_MAPVIEW,
  TOOLTIP_SET_DEFAULT_MAPVIEW,
} from '../../../constants/appConstants';
import { UNABLE_TO_SET_HOME_LOCATION } from '../../../constants/errorConstants';
import {
  ADD_USER_DEFAULT_LOCATION,
  NO_DRIVERS_ASSOCIATED_WITH_PLANT_TEXT,
  REMOVE_USER_DEFAULT_LOCATION,
} from '../../../constants/successConstants';
import { refreshGeoTraxState } from '../../../services/appService';
import { getOrdersByOrderUrls, getOrdersForMap } from '../../../services/ordersService';
import { fetchPlantListAndMapFilters } from '../../../services/plantsService';
import { setCurrentRegion } from '../../../services/regionsService';
import { getRequest } from '../../../services/requestService';
import {
  fetchOrderTicketList,
  selectOrderMarker,
  selectTruckMarker,
} from '../../../services/ticketsService';
import { ReduxState } from '../../../store';
import { openFailModal } from '../../../store/actions/errorModalActions';
import {
  MarkerInfo,
  MarkerSelectedFrom,
  selectMarker,
} from '../../../store/actions/mapviewActions';
import { setCurrentTab, updateToFieldValue } from '../../../store/actions/messageActions';
import { removeUserOverride, setUserOverride } from '../../../store/actions/regionActions';
import setSidebarSelected from '../../../store/actions/sidebarActions';
import { openSnackbar } from '../../../store/actions/snackbarActions';
import { selectTicket } from '../../../store/actions/ticketActions';
import {
  OpenMapMarker,
  sortedOpenMarkersMatchingMapFilter,
} from '../../../store/selectors/mapViewSelectors';
import { ConnectedDispatchFunction, MapMarker, Marker, PositionDetailsDto } from '../../../types';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import { determineRegionId, getIdFromUrl, setFavicon } from '../../../util/appUtil';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import { isValidLatLng } from '../../../util/geoUtil';
import {
  createTrackableMapMarkersFromPositions,
  determineMarkerHasValidLatLng,
  determineMarkerLocation,
  isMapMarker,
  isMarkerType,
} from '../../../util/markerUtil';
import { messageDrivers, truncateDriver } from '../../../util/messageUtil';
import { DriverWithTruck } from '../../../util/positionsUtil';
import { setTicketsToPositions } from '../../../util/ticketUtil';
import VehicleAndOrderSearch from '../../shared/header/VehicleAndOrderSearch';
import MapFiltersDropdown from './MapFilters';
import style from './MapView.module.css';
import GeotraxMap from './MapWrapper';
import { MarkerType } from './TTMap';
import { DriverDetail } from '../../../store/selectors/messageSelectors';

export class MapView extends Component<MapViewProps, MapViewState> {
  static defaultProps = {
    drivers: [],
    currentRegion: {
      url: '',
      location: {
        latitude: 0,
        longitude: 0,
      },
      name: '',
    },
    messages: { partiesList: [] },
    positions: [],
    showExternalTrucks: true,
    showInternalTrucks: true,
    ticketsList: [],
    selectedProductLine: ProductLine.None,
    defaultZoom: 9,
    selectedMarkerDefaultZoom: 15,
  };

  constructor(props: MapViewProps) {
    super(props);
    this.state = {
      ordersListTickets: [],
      ordersListMap: [],
      ordersUrlList: '',
      mounted: false,
      lat: this.props.currentRegion?.location?.latitude,
      lng: this.props.currentRegion?.location?.longitude,
      zoom: MapView.defaultProps.defaultZoom,
      shouldCheckCenter: false,
      mustResolveTruckFromQueryParam: false,
    };
  }

  async componentDidMount() {
    this.findExternalTruckIfRequested();
    if (this.props.currentRegion && this.props.selectedProductLine) {
      this.props.fetchPlantListAndMapFilters(
        this.props.currentRegion.url,
        this.props.selectedProductLine,
        this.props.userUrl,
      );
    }

    this.handleInitialDefaultMapCenter();

    document.title = 'GeoTrax - TruckTrax';
    setFavicon('/favicon-geotrax.ico');
    this.setState({ mounted: true });

    await this.fetchOrdersBasedOnTickets();
    if (this.props.showJobsites) {
      await this.orderListOn(this.props.ordersStartDate, this.props.ordersEndDate);
    }
  }

  handleInitialDefaultMapCenter = () => {
    const isCentered = this.ensureMapIsCenteredOnOpenMarkerMatchingFilters(true);
    if (isCentered) {
      return;
    }

    const { userOverrides, currentRegion } = this.props;
    if (
      userOverrides &&
      userOverrides.regionUrl === currentRegion?.url &&
      isValidLatLng(userOverrides?.lat, userOverrides?.lng)
    ) {
      this.setState({
        lat: userOverrides.lat,
        lng: userOverrides.lng,
        zoom: userOverrides.zoom,
      });
      return;
    }

    const regionLatitude = currentRegion?.location?.latitude;
    const regionLongitude = currentRegion?.location?.longitude;

    if (isValidLatLng(regionLatitude, regionLongitude)) {
      this.setState({
        lat: regionLatitude!,
        lng: regionLongitude!,
        zoom: MapView.defaultProps.defaultZoom,
      });
    }
  };

  private async findExternalTruckIfRequested(): Promise<void> {
    const { location, currentRegion } = this.props;
    const originalSearch = new URLSearchParams(location.search);
    const truckExternalId = originalSearch.get('truckExternalId');
    const regionId = originalSearch.get('regionId');

    if (!truckExternalId || !regionId) {
      return;
    }

    const requestedRegion = this.props.assignedRegions?.find(r => getIdFromUrl(r.url) === regionId);
    if (!requestedRegion) {
      this.clearTruckSelectionQueryParams();
      return;
    }

    const url = `${getGeotraxBaseUrl()}${TRUCKS_PATH}?externalId=${truckExternalId}&regionUrl=${encodeURIComponent(requestedRegion.url!)}`;
    const response: AxiosResponse<PagedEntityDto<TruckDto>> = await getRequest(url);

    const [truck] = response?.data?.items || [];
    if (!truck) {
      this.clearTruckSelectionQueryParams();
      return;
    }

    const { selectedProductLine } = this.props;
    const truckBelongsToProductLine =
      truck.primaryProductLine === selectedProductLine ||
      truck.secondaryProductLine === selectedProductLine;

    if (!truckBelongsToProductLine || requestedRegion.url !== currentRegion?.url) {
      const { userUrl, messageSubscription } = this.props;

      this.props.refreshGeoTraxState(
        requestedRegion,
        truck.primaryProductLine,
        userUrl,
        messageSubscription,
      );
    }

    this.setState({
      requestedExternalTruck: truck,
    });
  }

  private clearTruckSelectionQueryParams(): void {
    const { location, history } = this.props;
    const originalSearch = new URLSearchParams(location.search);

    originalSearch.delete('truckExternalId');
    originalSearch.delete('regionId');
    history.replace({ search: originalSearch.toString() });
  }

  private selectExternalTruckIfRequested(): void {
    const { location } = this.props;
    const { requestedTruckMarker, forceUpdateRequestedTruckMarker } = this.state;

    const originalSearch = new URLSearchParams(location.search);
    const truckExternalId = originalSearch.get('truckExternalId');
    if (!truckExternalId && !forceUpdateRequestedTruckMarker) {
      return;
    }

    if (!requestedTruckMarker) {
      return;
    }

    const currentRegionId = determineRegionId(this.props.currentRegion as unknown as UserRegionDto);
    if (!currentRegionId || !this.props.selectedProductLine) {
      this.setState({
        mustResolveTruckFromQueryParam: true,
      });
      recordLog(
        LogLevel.Info,
        'Current Region and product line are required to open marker from ClearView!',
      );
      return;
    }

    this.props.selectTruckMarker(
      { ...requestedTruckMarker, selectedFrom: MarkerSelectedFrom.MapMarker } as MarkerType,
      requestedTruckMarker.data.ticket?.url,
    );

    this.setState({
      lat: requestedTruckMarker.data.lat,
      lng: requestedTruckMarker.data.lng,
      zoom: MapView.defaultProps.defaultZoom,
      forceUpdateRequestedTruckMarker: false,
    });

    this.clearTruckSelectionQueryParams();
  }

  async componentDidUpdate(prevProps: MapViewProps) {
    this.selectExternalTruckIfRequested();
    await this.fetchOrdersBasedOnTickets();

    // Re-fetch plant list and map filters when userPermission becomes available
    // This handles the case where fetchPlantListAndMapFilters was called before permissions loaded
    if (this.props.currentRegion?.url && this.props.selectedProductLine && this.props.userUrl) {
      const regionUrl = this.props.currentRegion.url;
      const prevRegionPermissions = prevProps.userPermission?.[regionUrl];
      const currentRegionPermissions = this.props.userPermission?.[regionUrl];

      // If permissions just became available for this region, re-fetch the filters
      if (!prevRegionPermissions && currentRegionPermissions) {
        this.props.fetchPlantListAndMapFilters(
          regionUrl,
          this.props.selectedProductLine,
          this.props.userUrl,
        );
      }
    }

    if (this.state.mustResolveTruckFromQueryParam) {
      this.findExternalTruckIfRequested();
    } else {
      this.handleMapCenterUpdate(prevProps);
    }
  }

  determineSelectedMarkerZoom = (selectedMarker?: Partial<MarkerInfo>): number => {
    const marker = selectedMarker ?? this.props.selectedMarker;
    const { zoom: currentZoom } = this.state;
    if (!marker) {
      return currentZoom;
    }

    const { selectedFrom } = marker;
    if (
      currentZoom === MapView.defaultProps.selectedMarkerDefaultZoom ||
      selectedFrom === MarkerSelectedFrom.MapMarker
    ) {
      return currentZoom;
    }

    const shouldUseSelectedMarkerZoom =
      selectedFrom === MarkerSelectedFrom.VehicleAndOrderSearch ||
      selectedFrom === MarkerSelectedFrom.TruckFolderList;
    if (shouldUseSelectedMarkerZoom) {
      return MapView.defaultProps.selectedMarkerDefaultZoom;
    }

    return currentZoom;
  };

  handleFilterChangingCenter = (prevProps: MapViewProps) => {
    if (this.state.shouldCheckCenter) {
      return;
    }

    const productLineChanged = prevProps.selectedProductLine !== this.props.selectedProductLine;
    const regionChanged = prevProps.currentRegion?.url !== this.props.currentRegion?.url;
    if (productLineChanged || regionChanged) {
      this.setState({
        shouldCheckCenter: true,
      });
    }
  };

  handleMapCenterUpdate = (prevProps: MapViewProps): void => {
    this.handleFilterChangingCenter(prevProps);
    let centeredOnExistingMarker = false;
    if (this.state.shouldCheckCenter) {
      centeredOnExistingMarker = this.ensureMapIsCenteredOnOpenMarkerMatchingFilters();
    }

    if (centeredOnExistingMarker) {
      return;
    }

    const { selectedMarker } = this.props;
    const productLineChanged = prevProps.selectedProductLine !== this.props.selectedProductLine;
    const regionChanged = prevProps.currentRegion?.url !== this.props.currentRegion?.url;
    const centerShouldBeChanged = productLineChanged || regionChanged;
    const selectedMarkerChanged = !isEqual(selectedMarker, prevProps.selectedMarker);
    const selectedMarkerLocation = determineMarkerLocation(selectedMarker);
    const selectedMarkerHasValidLocation =
      selectedMarkerLocation &&
      isValidLatLng(selectedMarkerLocation.lat, selectedMarkerLocation.lng);
    const selectedMarkerMatchesFilters =
      this.markerMatchesProductLineAndRegionFilter(selectedMarker);
    if (
      (selectedMarkerChanged || centerShouldBeChanged) &&
      selectedMarkerHasValidLocation &&
      selectedMarkerMatchesFilters
    ) {
      const zoom = this.determineSelectedMarkerZoom(selectedMarker);
      this.setState({
        lat: selectedMarkerLocation.lat,
        lng: selectedMarkerLocation.lng,
        zoom,
      });
      return;
    }

    if (!centerShouldBeChanged) {
      return;
    }

    const { userOverrides } = this.props;
    const shouldUseUserOverride =
      userOverrides?.regionUrl === this.props.currentRegion?.url &&
      isValidLatLng(userOverrides?.lat, userOverrides?.lng);
    if (centerShouldBeChanged && shouldUseUserOverride) {
      this.setState({
        lat: userOverrides.lat,
        lng: userOverrides.lng,
        zoom: userOverrides.zoom ?? MapView.defaultProps.defaultZoom,
      });
      return;
    }

    const { currentRegion } = this.props;
    const shouldUseRegionDefault = currentRegion?.location
      ? isValidLatLng(currentRegion?.location?.latitude, currentRegion?.location?.longitude)
      : false;
    if (centerShouldBeChanged && shouldUseRegionDefault) {
      this.setState({
        lat: currentRegion!.location!.latitude,
        lng: currentRegion!.location!.longitude,
        zoom: MapView.defaultProps.defaultZoom,
      });
    }
  };

  ensureMapIsCenteredOnOpenMarkerMatchingFilters = (ignoreShouldCheckCenter: boolean = false) => {
    const centeringNotNecessary = !this.state.shouldCheckCenter;
    if (!ignoreShouldCheckCenter && centeringNotNecessary) {
      return false;
    }

    const { sortedOpenMapMarkers, selectedProductLine } = this.props;
    if (!selectedProductLine) {
      return false;
    }

    const hasSelected = sortedOpenMapMarkers.length > 0;
    if (!hasSelected) {
      return false;
    }

    const selectedProductLineMarker = sortedOpenMapMarkers.at(0);
    const hasValidLocation = determineMarkerHasValidLatLng(selectedProductLineMarker);
    const markerMatchesFilters =
      this.markerMatchesProductLineAndRegionFilter(selectedProductLineMarker);

    if (!selectedProductLineMarker || !hasValidLocation || !markerMatchesFilters) {
      return false;
    }

    const location = determineMarkerLocation(selectedProductLineMarker);
    if (!isValidLatLng(location?.lat, location?.lng)) {
      return false;
    }

    this.setState({
      lat: location!.lat,
      lng: location!.lng,
      shouldCheckCenter: false,
      zoom: MapView.defaultProps.selectedMarkerDefaultZoom,
    });

    return true;
  };

  markerMatchesProductLineAndRegionFilter = (marker: unknown): boolean =>
    this.markerMatchesSelectedProductLine(marker) && this.markerMatchesSelectedRegion(marker);

  markerMatchesSelectedProductLine = (marker: unknown): boolean => {
    if (isMapMarker(marker)) {
      return marker.data?.positionDetails?.productLine === this.props.selectedProductLine;
    }

    if (isMarkerType(marker)) {
      return marker.positionDetails?.productLine === this.props.selectedProductLine;
    }
    return false;
  };

  markerMatchesSelectedRegion = (marker: unknown): boolean => {
    if (isMapMarker(marker)) {
      return marker.data?.positionDetails?.region?.url === this.props.currentRegion?.url;
    }

    if (isMarkerType(marker)) {
      return marker.positionDetails?.region?.url === this.props.currentRegion?.url;
    }
    return false;
  };

  componentWillUnmount() {
    this.setState({ mounted: false });
  }

  fetchOrdersBasedOnTickets = async () => {
    const hasTickets = this.props.ticketsList && this.props.ticketsList.length > 0;
    if (!hasTickets && this.state.ordersListTickets?.length !== 0) {
      if (this.state.mounted) {
        this.setState({
          ordersListTickets: [],
          ordersUrlList: '',
        });
      }
      return;
    }

    const orderUrls = this.props
      .ticketsList!.filter(ticket => ticket.order && ticket.order.url)
      .map(ticket => ticket.order?.url);
    // de-duplicate the urls, and then sort them.
    const uniqueUrls = [...new Set(orderUrls)].sort();
    const orderUrlList = uniqueUrls.join(',');
    if (!orderUrlList) {
      recordLog(
        LogLevel.Debug,
        '[fetchOrdersBasedOnTickets] avoiding call to getOrdersByOrderUrls orderUrls must be valid',
      );
      return;
    }
    if (orderUrlList !== this.state.ordersUrlList) {
      this.setState(
        {
          ordersUrlList: orderUrlList,
        },
        async () => {
          const ordersListTickets = (
            (await this.props.getOrdersByOrderUrls(orderUrlList, undefined, false)) || []
          )
            // Filter out orders that don't have a geoZone, generally because they have an invalid address
            .filter(order => order.geoZone?.zone.circle?.radius);
          if (this.state.mounted) {
            this.setState({
              ordersListTickets,
            });
          }
        },
      );
    }
  };

  orderListOn = async (startDate: Date, endDate: Date) => {
    const { currentRegion, selectedProductLine } = this.props;
    const ordersListMap = (
      await this.props.getOrdersForMap(
        currentRegion!.url,
        new DateWrapper(startDate).startOfDay.date,
        new DateWrapper(endDate).endOfDay.date,
        selectedProductLine!,
      )
    )
      // Filter out orders that don't have a geoZone, generally because they have an invalid address
      .filter(order => order.geoZone?.zone.circle?.radius);

    if (this.state.mounted) {
      this.setState({
        ordersListMap,
      });
    }
  };

  orderListOff = async () => {
    this.setState({
      ordersListMap: [],
    });
  };

  driverInfoMessageCallback = (driver: DriverDetail) => {
    const driverDto = truncateDriver(driver);
    this.props.messageDrivers([driverDto]);
    return true;
  };

  updateToFieldValueWithPlantDrivers = (url: string) => {
    const filteredDrivers = this.props.drivers?.filter(
      driver =>
        driver?.primaryPlant?.url === url || this.isDriverCurrentTicketForPlant(url, driver),
    );

    if (filteredDrivers && filteredDrivers.length > 0) {
      const truncatedDrivers = filteredDrivers.map(filteredDriver =>
        truncateDriver(filteredDriver),
      );
      this.props.messageDrivers(truncatedDrivers);
    } else {
      this.props.openSnackbar({
        snackbarBody: NO_DRIVERS_ASSOCIATED_WITH_PLANT_TEXT,
        dataTest: 'no-drivers-to-message',
        snackbarType: DEFAULT,
      });
    }
    return true;
  };

  isDriverCurrentTicketForPlant(plantUrl: string, driver: DriverWithTruck): boolean {
    const driversTickets = this.props.ticketsList?.filter(t => t.driver?.url === driver.url);
    if (!driversTickets?.length) {
      return false;
    }

    const [currentTicket] = driversTickets.toSorted((lhs, rhs) => {
      const ticketedDiff = DateWrapper.differenceBetween(
        lhs.ticketedTimestamp,
        rhs.ticketedTimestamp,
      ).totalMilliseconds;
      if (ticketedDiff === 0) {
        return DateWrapper.differenceBetween(lhs.createdTimestamp, rhs.createdTimestamp)
          .totalMilliseconds;
      }
      return ticketedDiff;
    });

    return currentTicket?.plant?.url === plantUrl;
  }

  selectTicket = (markerData: Marker) => {
    this.props.selectTicket(markerData.ticket);
    this.props.setSidebarSelected(TICKETS_SELECTED);
    this.props.selectTruckMarker(markerData as MarkerType);
    return true;
  };

  orderDetailsCallback = (targetMarker: MarkerType) => {
    const orderId = (targetMarker.markerData as OrderDto).id;
    this.props.history.push(`/geotrax/orders/all/${orderId}`);
  };

  messageDriversOnOrder = async (order?: OrderDto) => {
    if (!order?.id) {
      return;
    }

    const orderTicketList: TicketDto[] = await this.props.fetchOrderTicketList(order.id);
    const drivers = orderTicketList
      .filter(ticket => !!ticket.driver?.url && !!ticket.externalInfo?.driverName && !ticket.closed)
      .map(ticket => ({
        fullName: ticket.externalInfo?.driverName,
        url: ticket.driver?.url,
      }));

    this.props.messageDrivers(drivers);
  };

  render() {
    const { currentRegion, ticketsList, positions, selectedProductLine } = this.props;

    const renderProductLine = (productLine: ProductLine) => {
      if (productLine === READYMIX_VALUE) {
        return READYMIX_STRING;
      }
      return productLine;
    };
    const titleRegion = currentRegion?.name ?? '';
    const title = `${titleRegion} ${renderProductLine(selectedProductLine!) ?? ''} Trucks`;
    setTicketsToPositions(ticketsList ?? [], positions ?? []);
    const elementsLeft = (
      <div className={style.flexGroup}>
        <MapFiltersDropdown />
        <h5 className={style.h5}>{title}</h5>
      </div>
    );
    const { ordersListTickets, ordersListMap } = this.state;
    const ordersList =
      ordersListMap && ordersListMap.length > 0 ? ordersListMap : ordersListTickets;

    if (currentRegion?.name === undefined) {
      return <Loader />;
    }

    const defaultLocation = this.determineDefaultLocation();
    const currentLocation = this.determineLocation(defaultLocation);

    return (
      <HeaderAndContainer
        elementsLeft={elementsLeft}
        elementsRight={
          <Stack direction="row">
            <VehicleAndOrderSearch ordersList={ordersList} />
            <Tooltip
              title={
                this.props.userOverrides
                  ? TOOLTIP_REMOVE_DEFAULT_MAPVIEW
                  : TOOLTIP_SET_DEFAULT_MAPVIEW
              }
            >
              <IconButton onClick={this.toggleDefaultLocation}>
                <AddLocationIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        }
        containerComponent={
          <GeotraxMap
            handleZoomChange={this.handleZoomChange}
            currentZoom={this.state.zoom}
            handleCenterChange={this.handleCenterChange}
            location={currentLocation}
            driverInfoMessageCallback={this.handleDriverInfoMessageCallback}
            updateToFieldValueWithPlantDrivers={this.handleUpdateToFieldValueWithPlantDrivers}
            ticketInfoTicketsCallBack={this.handleSelectTicket}
            orderDetailsCallback={this.orderDetailsCallback}
            messageDriversOnOrder={this.messageDriversOnOrder}
            ordersList={ordersList}
            orderListOn={this.orderListOn}
            orderListOff={this.orderListOff}
            allTrucks={this.props.allTrucks}
          />
        }
      />
    );
  }

  determineDefaultLocation = (): { lat: number; lng: number } => {
    const hasUserOverrides =
      this.props.userOverrides && this.props.userOverrides.lat && this.props.userOverrides.lng;
    const overridesMatchProductLine =
      this.props.currentRegion?.url === this.props.userOverrides?.regionUrl;
    if (hasUserOverrides && overridesMatchProductLine) {
      return { lat: this.props.userOverrides.lat, lng: this.props.userOverrides.lng };
    }

    return {
      lat: this.props.currentRegion?.location.latitude ?? 0,
      lng: this.props.currentRegion?.location.longitude ?? 0,
    };
  };

  determineLocation = (defaultLocation: {
    lat: number;
    lng: number;
  }): { lat: number; lng: number } => {
    if (isValidLatLng(this.state.lat, this.state.lng)) {
      return { lat: this.state.lat!, lng: this.state.lng! };
    }
    return defaultLocation;
  };

  handleUpdateToFieldValueWithPlantDrivers = (url: string) =>
    this.updateToFieldValueWithPlantDrivers(url);

  handleDriverInfoMessageCallback = (driver: any) => this.driverInfoMessageCallback(driver);

  handleSelectTicket = (ticket: Marker) => this.selectTicket(ticket);

  handleRemoveHome = () => {
    this.props.removeUserOverride();
    this.props.openSnackbar({
      snackbarBody: REMOVE_USER_DEFAULT_LOCATION,
      dataTest: 'home-location-removed',
      snackbarType: SUCCESS,
    });
  };

  handleZoomChange = (zoom: number) => {
    if (this.state.zoom === zoom) return;
    this.setState({
      zoom,
    });
  };

  handleCenterChange = (center: { lat: number; lng: number }) => {
    const { lat, lng } = center;
    if (this.state.lat === lat && this.state.lng === lng) return;
    this.setState({
      lat,
      lng,
    });
  };

  toggleDefaultLocation = () => {
    const { lat, lng, zoom } = this.state;

    if (this.props.userOverrides) {
      this.handleRemoveHome();
      return;
    }
    try {
      if (!this.props.currentRegion?.url || !lat || !lng) {
        recordLog(
          LogLevel.Debug,
          'Can not set the user map center override with a region and valid lat and lng',
        );
        return;
      }

      this.props.setUserOverride({
        lat,
        lng,
        zoom,
        regionUrl: this.props.currentRegion.url,
      });
      this.props.openSnackbar({
        snackbarBody: ADD_USER_DEFAULT_LOCATION,
        dataTest: 'home-location-success',
        snackbarType: SUCCESS,
      });
    } catch (e: any) {
      this.props.openFailModal(e.toString(), UNABLE_TO_SET_HOME_LOCATION);
    }
  };

  static getDerivedStateFromProps(nextProps: MapViewProps, prevState: MapViewState) {
    const { requestedExternalTruck } = prevState;
    const markers = createTrackableMapMarkersFromPositions(
      nextProps?.positions || [],
      nextProps.currentRegion?.url || '',
    );

    const requestedTruckMarker = markers.find(
      m => m.data.positionDetails?.trackedObject?.url === requestedExternalTruck?.url,
    );

    const requestedTruckMarkerHasNoChanges =
      prevState.requestedTruckMarker?.markerId === requestedTruckMarker?.markerId &&
      prevState.requestedTruckMarker?.data.positionDetails?.distance?.text ===
        requestedTruckMarker?.data.positionDetails?.distance?.text &&
      prevState.requestedTruckMarker?.data.positionDetails?.duration?.value ===
        requestedTruckMarker?.data.positionDetails?.duration?.value &&
      prevState.requestedTruckMarker?.data.positionDetails?.duration?.text ===
        requestedTruckMarker?.data.positionDetails?.duration?.text;

    if (requestedTruckMarkerHasNoChanges) {
      return null;
    }

    return {
      requestedTruckMarker,
      forceUpdateRequestedTruckMarker: true,
    };
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    currentRegion: state.currentRegion,
    assignedRegions: state.assignedRegionList,
    drivers: state.driverList.drivers,
    messages: state.messages,
    currentTab: state.currentTab.currentTab,
    positions: state.positionList,
    ticketsList: state.ticketsList,
    selectedProductLine: state.selectedProductLine,
    showJobsites: state.mapFilters.showJobsites,
    ordersStartDate: state.mapFilters.ordersStartDate,
    ordersEndDate: state.mapFilters.ordersEndDate,
    userUrl: state.userUrl,
    userOverrides: state.userOverrides,
    allTrucks: state.allTrucks,
    allDrivers: state.allDrivers,
    messageSubscription: state.messageSubscription,
    selectedMarker: state.selectedMarker,
    activeMarkers: state.activeMarkers,
    popupToggleMap: state.popupToggleMap,
    sortedOpenMapMarkers: sortedOpenMarkersMatchingMapFilter(state),
    userPermission: state.userPermission || {},
  };
}
export interface Location {
  latitude: number;
  longitude: number;
}

export interface Region {
  url: string;
  location: Location;
  name: string;
}

export interface Message {
  partiesList: any[];
}

interface MapViewOwnProps {
  selectedMarker: Partial<MarkerInfo>;
  sortedOpenMapMarkers: OpenMapMarker[];
}

export interface MapViewProps extends MapViewOwnProps, MapViewDispatchProps {
  currentRegion?: Region;
  assignedRegions?: UserRegionDto[];
  drivers?: DriverWithTruck[];
  positions?: PositionDetailsDto[];
  allTrucks?: TruckDto[];
  ticketsList?: TicketDto[];
  selectedProductLine?: ProductLine;
  history: History;
  showJobsites: boolean;
  ordersStartDate: Date;
  ordersEndDate: Date;
  userUrl: string;
  userOverrides: { lat: number; lng: number; zoom: number; regionUrl: string };
  messageSubscription: ReduxState['messageSubscription'];
  userPermission: ReduxState['userPermission'];
  // router
  location: BrowserLocation;
}

interface MapViewDispatchProps {
  selectTicket: (ticket: UrlKeyDto) => void;
  selectTruckMarker: (marker: MarkerType, ticketUrl?: string, truckAlias?: string) => void;
  setSidebarSelected: (sideBar: string) => void;
  getOrdersByOrderUrls: (
    orderUrl: string,
    region?: string,
    includeLineItems?: boolean,
  ) => Promise<OrderDto[]>;
  getOrdersForMap: (
    currentRegionUrl: string,
    startDate: Date,
    endDate: Date,
    selectedProductLine: ProductLine,
  ) => Promise<OrderDto[]>;
  fetchOrderTicketList: ConnectedDispatchFunction<typeof fetchOrderTicketList>;
  fetchPlantListAndMapFilters: ConnectedDispatchFunction<typeof fetchPlantListAndMapFilters>;
  messageDrivers: ConnectedDispatchFunction<typeof messageDrivers>;
  setUserOverride: ConnectedDispatchFunction<typeof setUserOverride>;
  removeUserOverride: ConnectedDispatchFunction<typeof removeUserOverride>;
  openSnackbar: ConnectedDispatchFunction<typeof openSnackbar>;
  openFailModal: ConnectedDispatchFunction<typeof openFailModal>;
  refreshGeoTraxState: typeof refreshGeoTraxState;
}

export interface MapViewState {
  ordersListTickets: OrderDto[];
  ordersListMap: OrderDto[];
  ordersUrlList: string;
  mounted: boolean;
  lat?: number;
  lng?: number;
  zoom: number;
  requestedTruckMarker?: MapMarker;
  forceUpdateRequestedTruckMarker?: boolean;
  requestedExternalTruck?: TruckDto;
  shouldCheckCenter: boolean;
  mustResolveTruckFromQueryParam: boolean;
}

export default withRouter(
  connect<any, any, any, ReduxState>(mapStateToProps, {
    updateToFieldValue,
    setCurrentRegion,
    setCurrentTab,
    selectTicket,
    selectTruckMarker,
    setSidebarSelected,
    getOrdersByOrderUrls,
    getOrdersForMap,
    fetchOrderTicketList,
    fetchPlantListAndMapFilters,
    messageDrivers,
    setUserOverride,
    removeUserOverride,
    openSnackbar,
    openFailModal,
    refreshGeoTraxState,
    selectMarker,
    selectOrderMarker,
  })(MapView),
);
